import { useId } from 'react';

interface BrandMarkProps {
    className?: string;
    title?: string;
}

/**
 * Vector redraw of the company "M" mark: a rounded tile split into a deep-blue
 * top and a light-sky bottom by a white electric-pulse stroke.
 */
const BrandMark = ({ className = "", title }: BrandMarkProps) => {
    const uid = useId().replace(/:/g, "");
    const id = (name: string) => `${name}-${uid}`;

    // Centre-line of the white pulse stroke (viewBox 0..200)
    const pulse = "M0 124 H19 L80 30 L79 177 L163 30 L169 121 L200 111";

    return (
        <svg
            viewBox="0 0 200 200"
            className={className}
            role={title ? "img" : undefined}
            aria-hidden={title ? undefined : true}
            aria-label={title}
        >
            <defs>
                <clipPath id={id("tile")}>
                    <rect x="0" y="0" width="200" height="200" rx="46" />
                </clipPath>
                <linearGradient id={id("light")} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#e6f6fd" />
                    <stop offset="1" stopColor="#a9ddf6" />
                </linearGradient>
                <radialGradient id={id("blue")} cx="0.55" cy="0.12" r="0.95">
                    <stop offset="0" stopColor="#35b6f7" />
                    <stop offset="0.55" stopColor="#1f86d6" />
                    <stop offset="1" stopColor="#1b5fa8" />
                </radialGradient>
                <linearGradient id={id("gloss")} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#fff" stopOpacity="0.35" />
                    <stop offset="0.5" stopColor="#fff" stopOpacity="0" />
                </linearGradient>
            </defs>

            <g clipPath={`url(#${id("tile")})`}>
                {/* light lower half */}
                <rect width="200" height="200" fill={`url(#${id("light")})`} />
                {/* deep-blue upper half, bounded by the pulse */}
                <path
                    d="M0 0 V124 H19 L80 30 L79 112 L116 112 L163 30 L169 121 L200 111 V0 Z"
                    fill={`url(#${id("blue")})`}
                />
                {/* soft glossy highlight */}
                <rect width="200" height="110" fill={`url(#${id("gloss")})`} />
                {/* halo + white pulse stroke */}
                <path d={pulse} fill="none" stroke="#8fd3f5" strokeWidth="27" strokeLinejoin="round" strokeLinecap="round" opacity="0.55" />
                <path d={pulse} fill="none" stroke="#fff" strokeWidth="21" strokeLinejoin="round" strokeLinecap="round" />
            </g>

            {/* crisp rim */}
            <rect x="1.5" y="1.5" width="197" height="197" rx="45" fill="none" stroke="#1b69b4" strokeOpacity="0.55" strokeWidth="3" />
        </svg>
    );
};

export default BrandMark;
